import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import PresentationAttributeApi from '@/api/presentationAttribute'
import constant from '@/constant'
import { DS_CHART_PALETTE, resolveInkAwarePalette } from '@/business-logic/ds-chart-palette'

const _state = {
  presentationAttribute: {},
}

function setupColors(colors) {
  const COLORS_LENGTH = 30
  if (!colors || colors.length === 0) {
    return new Array(COLORS_LENGTH).fill('#E70E68')
  }

  const result = [...colors]
  const { length } = result
  for (let i = 0; i < COLORS_LENGTH; i++) {
    if (result[i] === undefined) {
      result[i] = result[i % length]
    }
  }
  return result
}

const mutations = {
  setPresentationAttribute(state, payload) {
    state.presentationAttribute = payload
  },
  updatePresentationAttribute(state, payload) {
    Vue.set(state.presentationAttribute, payload.attributeName, payload.attributeValue)
  },
}

const actions = {
  async fetchPresentationAttributes(_, presentationId) {
    return PresentationAttributeApi.getPresentationAttributes(presentationId)
  },

  async getPresentationAttributeAction({ dispatch, commit, rootGetters }, presentationId) {
    try {
      const resp = await dispatch('optimizeEditorLoadModule/getPresentationAttributesWithCacheAction', {
        presentationId,
        maxAgeInSeconds: rootGetters['optimizeEditorLoadModule/maxPresentationCacheTimeInSeconds'],
      }, { root: true })
      commit('setPresentationAttribute', resp.body)
      return resp.body
    } catch (error) {
      // Captured raw so services/sentry beforeSend drops status-0/4xx Responses and relabels a 5xx.
      Sentry.captureException(error, { extra: { presentationId } })
      return {}
    }
  },

  async updatePresentationAttributeAction({ commit }, { presentationId, payload }) {
    commit('updatePresentationAttribute', payload)
    const resp = await PresentationAttributeApi.patchPresentationAttribute(presentationId, payload)
    return resp.body
  },
}

const _getters = {
  presentationUsesDsFallbackPalette: (state, _, __, rootGetters) => {
    return Boolean(!state.presentationAttribute?.colorPalette?.colors && rootGetters['featureToggles/canUseDsChartPalette'])
  },
  presentationUsesInkAwarePalette: (state, getters) => {
    return Boolean(state.presentationAttribute?.colorPalette?.inkAware || getters.presentationUsesDsFallbackPalette)
  },
  presentationColorPalette: (state, getters, rootState, rootGetters) => {
    const savedPalette = state.presentationAttribute?.colorPalette
    const usesDsFallback = getters.presentationUsesDsFallbackPalette
    if (getters.presentationUsesInkAwarePalette) {
      const palette = usesDsFallback ? DS_CHART_PALETTE : savedPalette
      const slideTextColour = rootState.slideActive?.textColour
        || rootGetters['themeModule/currentPresentationTheme']?.textColour
      return setupColors(resolveInkAwarePalette(palette.colors, slideTextColour))
    }
    let colors = constant.colors2025
    if (state.presentationAttribute?.colorPalette?.colors) {
      colors = state.presentationAttribute.colorPalette.colors
    }
    return setupColors(colors)
  },
  presentationLighterColorPalette: (state, getters) => {
    if (getters.presentationUsesDsFallbackPalette) {
      return setupColors(DS_CHART_PALETTE.lighterColors)
    }
    let lighterColors = constant.lighterColors2025
    if (state.presentationAttribute?.colorPalette?.lighterColors) {
      lighterColors = state.presentationAttribute.colorPalette.lighterColors
    }
    return setupColors(lighterColors)
  },

  presentationAttributeColorPalette: (state) => {
    return state.presentationAttribute.colorPalette || {}
  },
}

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
}
